import React from 'react';
import Card, { CardShadow, CardStyles } from '@app/common/components/cards/Card';
import Button from '@app/common/components/Button';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useGetSubscription } from '@app/hooks/query/useSubscriptionData';
import { useSelector } from 'react-redux';
import { getAgency, getUserInfo } from '@app/selectors/user';
import { Spinner, SpinnerSizes } from '@app/common/design-system/spinner';
import { formatDateStandard, getAgencyTimezoneFromUser } from '@app/utils/dateUtils';
import { isAgencyUserAccount } from '@app/utils/userUtils';

import styles from './CurrentBillingPeriod.module.scss';
import cx from 'classnames';

function formatPrice(price: number | string): string {
    if (typeof price === 'number') {
        return `$${price.toFixed(2)}`;
    } else if (typeof price === 'string') {
        return price;
    }
    return '$0.00';
}

const CurrentBillingPeriod = () => {
    const agency = useSelector(getAgency);
    const loggedInUser = useSelector(getUserInfo);

    const { isLoading, isSuccess, data: subscription } = useGetSubscription(agency.id, undefined, undefined);

    const customerPortalSessionMutation = useMutation({
        mutationFn: () => {
            return axios.get(`/api/agency/account/create-customer-portal-session`).then(response => response.data);
        },
        onSuccess: data => {
            window.open(data.url, '_blank');
        }
    });

    return (
        <Card style={CardStyles.SQUARE} shadow={CardShadow.SMALL} className={styles.currentBillingPeriodCard}>
            <h2 className={styles.title}>Current Billing Period</h2>
            {isLoading && <Spinner size={SpinnerSizes.MEDIUM} />}
            {isSuccess && subscription && (
                <>
                    <div className={styles.priceSection}>
                        <p className={cx(styles.nextInvoice, styles.label)}>Next Invoice</p>
                        <p className={cx(styles.dueDate, styles.subLabel)}>
                            Due on {formatDateStandard(subscription.dueDate, getAgencyTimezoneFromUser(loggedInUser))}
                        </p>
                        <p className={cx(styles.price, styles.largeText)}>
                            {formatPrice(subscription.nextInvoiceCost)}
                        </p>
                        <p className={cx(styles.currency, styles.label)}>{subscription.currency}</p>
                        <p className={cx(styles.taxDescription, styles.smallText)}>
                            ({subscription.nextInvoiceTaxDescription})
                        </p>
                    </div>
                    <div>
                        <div className={styles.row}>
                            <p className={styles.label}>Period Start</p>
                            <p className={styles.value}>
                                {formatDateStandard(
                                    subscription.currentPeriodStart,
                                    getAgencyTimezoneFromUser(loggedInUser)
                                )}
                            </p>
                        </div>
                        <div className={styles.row}>
                            <p className={styles.label}>Period End</p>
                            <p className={styles.value}>
                                {formatDateStandard(
                                    subscription.currentPeriodEnd,
                                    getAgencyTimezoneFromUser(loggedInUser)
                                )}
                            </p>
                        </div>
                        <div className={styles.row}>
                            <p className={styles.label}>Days Remaining</p>
                            <p className={styles.value}>{subscription.remainingDays}</p>
                        </div>
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.row}>
                        <p className={styles.label}>What you’ve sent so far</p>
                        {/* Hide this until we have new reports feature ready */}
                        {/* <p className={styles.value}>
                            <Link className={styles.link} to="/user/agency/reports">
                                View All
                            </Link>
                        </p> */}
                    </div>
                    <div>
                        {isAgencyUserAccount(loggedInUser.accountType) && (
                            <div className={styles.row}>
                                <p className={styles.label}>Residential Tenancy Agreements</p>
                                <p className={styles.value}>{subscription.counts.agreements}</p>
                            </div>
                        )}
                        <div className={styles.row}>
                            <p className={styles.label}>Upload a Docs</p>
                            <p className={styles.value}>{subscription.counts.documents}</p>
                        </div>
                        {/* Hide this until we have the EOC feature ready */}
                        {/* <div className={styles.row}>
                            <p className={styles.label}>Exchange of Contracts</p>
                            <p className={styles.value}>{subscription.counts.eoc}</p>
                        </div> */}
                    </div>
                    <Button
                        className={styles.viewInvoicesButton}
                        height={32}
                        type="button"
                        secondary
                        loading={customerPortalSessionMutation.isLoading}
                        onClick={() => customerPortalSessionMutation.mutate()}
                    >
                        View Invoices
                    </Button>
                </>
            )}
        </Card>
    );
};

export default CurrentBillingPeriod;
